import { useEffect, useMemo, useState } from "react"

import { SquareIcon, RatioIcon, MoveHorizontalIcon, MoveVerticalIcon, XIcon } from "lucide-react"
import { ToggleOptionGroup, ToggleOptionGroupItem, DefaultTooltip, Select, InputGroup, InputGroupText, Input, DefaultPopover, Separator } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../utils"
import { UNIT_PREDEFINED_OPTIONS } from "../utils/predefined_sizes"

export default function GapProperty({ ...props }) {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  return <Gap key={device + selectedUtilities} {...props} />
}

function Gap({ name }) {
  const { value: all, setValue: setAll } = useCraftProp(`${name}.all`)
  const { value: x, setValue: setX } = useCraftProp(`${name}.x`)
  const { value: y, setValue: setY } = useCraftProp(`${name}.y`)

  const [unit, setUnit] = useState()
  const [type, setType] = useState()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (unit === undefined) {
      setType(x || y ? 'axis' : 'all')
      const value = x || y || all
      setUnit(!value ? 'predefined' : value.split('-')[1])
    }
  }, [unit, x, y, all])

  const handleChangeType = value => {
    if (!value) {
      return
    }

    setType(value)
    if (value === 'axis') {
      return setAll(undefined)
    }

    setX(undefined)
    setY(undefined)
  }

  return <>
    <InputTrigger all={all} type={type} x={x} y={y} onClick={() => setOpen(true)} />

    <DefaultPopover
      wrapperProps={{ open, onOpenChange: setOpen }}
      trigger={<div></div>}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'p-2', side: 'top', align: 'center' }}
    >
      <div className='flex justify-between items-center'>
        Gap
        <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <Separator className='m-0 my-2' />

      <div className='flex flex-col gap-1'>
        <ToggleOptionGroup size='lg' value={type} type='single' onValueChange={handleChangeType}>
          <ToggleOptionGroupItem value='all'>
            <DefaultTooltip content='All' triggerProps={{ className: 'w-full flex justify-center' }}>
              <SquareIcon size='14' />
            </DefaultTooltip>
          </ToggleOptionGroupItem>
          <ToggleOptionGroupItem value='axis'>
            <DefaultTooltip content='Axis' triggerProps={{ className: 'w-full flex justify-center' }}>
              <RatioIcon size='14' />
            </DefaultTooltip>
          </ToggleOptionGroupItem>
        </ToggleOptionGroup>

        {type === 'all' && <>
          <div className='flex gap-1'>
            <InputItem
              unit={unit}
              name={`${name}.all`}
            />
            <Select
              value={unit}
              onChange={option => setUnit(option?.value)}
              options={UNIT_PREDEFINED_OPTIONS}
            />
          </div>
        </>}

        {type === 'axis' && <>
          <div className='flex flex-col gap-1'>

            <Select
              value={unit}
              onChange={option => setUnit(option?.value)}
              options={UNIT_PREDEFINED_OPTIONS}
            />

            <div className='flex gap-1'>

              <InputItem
                unit={unit}
                name={`${name}.x`}
                icon={<MoveHorizontalIcon size='14' />}
                tooltip='Horizontal'
              />

              <InputItem
                unit={unit}
                name={`${name}.y`}
                icon={<MoveVerticalIcon size='14' />}
                tooltip='Vertical'
              />
            </div>
          </div>
        </>}

      </div>

    </DefaultPopover>
  </>
}

const InputTrigger = ({ all, type, x, y, onClick }) => {
  const inputValue = useMemo(() => {
    let response = ''
    const xValue = x
    const yValue = y
    const allValue = all
    if (type === 'axis') {
      response = [xValue && `X: ${xValue}`, yValue && `Y: ${yValue}`].filter(Boolean).join(', ')
    } else {
      response = allValue
    }

    response = response ? response.replaceAll('-', '').replaceAll('predefined', '') : ''
    return !response.match(/^[\p{L}]+$/u) && response
  }, [type, x, y, all])

  return <Input value={inputValue || ''} readOnly variant='accent' onClick={onClick} />
}

const InputItem = ({ unit, name, icon, tooltip }) => {
  const { value: craftValue, setValue: setCraftValue } = useCraftProp(name)
  const [value, setValue] = useState()

  useEffect(() => {
    const newValue = `${value ? `${value}-${unit}` : ''}`
    if (value !== undefined && newValue !== craftValue) {
      setCraftValue(newValue)
    }
  }, [unit, value, setCraftValue, craftValue])

  useEffect(() => {
    if (craftValue && value === undefined) {
      setValue(craftValue.split('-')[0])
    }
  }, [craftValue, setValue, value])

  return <>
    <div className='col-span-2 w-full'>
      <InputGroup>
        {icon && <>
          <DefaultTooltip content={tooltip}>
            <InputGroupText addonType='prepend'>
              {icon}
            </InputGroupText>
          </DefaultTooltip>
        </>}
        <Input
          variant='accent'
          type='number'
          size='sm'
          onChange={e => setValue(e.target.value)}
          value={value}
        />
      </InputGroup>
    </div>
  </>
}
